import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Home, Music, Download, LogIn, User, Menu, X, Guitar, BookOpen, Moon, Sun, Headphones, Smartphone, Store, Newspaper, Search, ChevronDown, Wrench } from "lucide-react";
import { useAuth, useIsAdmin } from "@/hooks/useAuth";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTheme } from "@/hooks/useTheme";
import { CATEGORIAS_HINOS } from "@/hooks/useHinos";
import { useHinos } from "@/hooks/useHinos";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import ProfileDrawer from "@/components/ProfileDrawer";
import NotificationBell from "@/components/NotificationBell";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeCategoria = location.pathname === "/hinos-avulsos" ? searchParams.get("categoria") : null;
  const { user } = useAuth();
  const { data: isAdmin } = useIsAdmin();
  const { data: hinos } = useHinos();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);

  const categoryCounts = useMemo(() => {
    if (!hinos) return {};
    const counts: Record<string, number> = { all: hinos.length };
    CATEGORIAS_HINOS.forEach(cat => {
      counts[cat.value] = hinos.filter(h => h.categoria === cat.value).length;
    });
    return counts;
  }, [hinos]);
  
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (user) {
      supabase.from("profiles").select("avatar_url, display_name").eq("user_id", user.id).single().then(({ data }) => {
        if (data) {
          const googleAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture;
          setAvatarUrl(data.avatar_url || googleAvatar || null);
          setDisplayName(data.display_name || user.user_metadata?.full_name || user.user_metadata?.name || null);
        } else {
          const googleAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture;
          setAvatarUrl(googleAvatar || null);
          setDisplayName(user.user_metadata?.full_name || user.user_metadata?.name || null);
        }
      });
    } else {
      setAvatarUrl(null);
      setDisplayName(null);
    }
  }, [user]);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const mainNavLinks = [
    { path: "/hinos-avulsos", label: "Hinos", icon: <Music className="h-4 w-4" /> },
    { path: "/baixar-cifras", label: "Cifras", icon: <Download className="h-4 w-4" /> },
    { path: "/louve-store", label: "Louve Store", icon: <Store className="h-4 w-4" /> },
  ];

  const toolsLinks = [
    { path: "/afinador", label: "Afinador", icon: <Guitar className="h-4 w-4" /> },
    { path: "/dicionario-acordes", label: "Dicionário de Acordes", icon: <Guitar className="h-4 w-4" /> },
    { path: "/campo-harmonico", label: "Campo Harmônico", icon: <Music className="h-4 w-4" /> },
    { path: "/aprender-violao", label: "Aprender Violão", icon: <BookOpen className="h-4 w-4" /> },
    { path: "/gerador-cifra", label: "Gerar Cifras", icon: <Search className="h-4 w-4" /> },
  ];

  const isToolActive = toolsLinks.some(t => isActive(t.path));

  const getUserInitials = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-500 border-b border-white/10 ${
        scrolled 
          ? "bg-[hsl(var(--navy-dark))]/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.4)]" 
          : "bg-[hsl(var(--navy-dark))]"
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-[72px] md:h-20">
            {/* Logo with spacing */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group mr-10">
              <div className="relative">
                <Guitar className="h-8 w-8 md:h-10 md:w-10 text-primary group-hover:text-[hsl(var(--gold-light))] transition-colors duration-300" />
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-2xl md:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-serif)' }}>LOUVE</span>
                <span className="text-[10px] md:text-xs text-white/40 tracking-[0.2em] uppercase">Hinos Avulsos</span>
              </div>
            </Link>

            {/* Separator */}
            <div className="hidden lg:block h-8 w-px bg-white/10 mr-6 shrink-0" />

            {/* Navigation - Desktop */}
            <nav className="hidden lg:flex items-center gap-1 flex-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path) 
                      ? "text-primary bg-primary/10" 
                      : "text-white/70 hover:text-primary hover:bg-white/5"
                  }`}
                >
                  {link.icon}
                  <span>{link.label}</span>
                </Link>
              ))}

              {/* Ferramentas Dropdown */}
              <div ref={toolsRef} className="relative">
                <button
                  onClick={() => setToolsOpen(!toolsOpen)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isToolActive || toolsOpen
                      ? "text-primary bg-primary/10"
                      : "text-white/70 hover:text-primary hover:bg-white/5"
                  }`}
                >
                  <Wrench className="h-4 w-4" />
                  <span>Ferramentas</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`} />
                </button>

                {toolsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-60 bg-[hsl(var(--navy-dark))] border border-white/10 rounded-xl shadow-2xl py-2 animate-fade-in z-50">
                    {toolsLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setToolsOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                          isActive(link.path)
                            ? "text-primary bg-primary/10"
                            : "text-white/70 hover:text-primary hover:bg-white/5"
                        }`}
                      >
                        {link.icon}
                        <span>{link.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Right side actions - Desktop */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              <Link
                to="/ouvir-hinos"
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-105 bg-gradient-to-r from-primary to-[hsl(var(--gold-light))] shadow-[0_4px_15px_hsl(var(--gold)/0.3)]"
              >
                <span className="flex items-center gap-1.5">
                  <Headphones className="h-4 w-4" />
                  Ouvir
                </span>
              </Link>

              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-lg text-white/50 hover:text-primary hover:bg-white/10 transition-all duration-200"
                aria-label={isDark ? "Modo claro" : "Modo escuro"}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {user && <NotificationBell />}

              {user ? (
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-full"
                >
                  <Avatar className="h-9 w-9 cursor-pointer border-2 border-white/20 hover:border-primary transition-colors">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">{getUserInitials()}</AvatarFallback>
                  </Avatar>
                </button>
              ) : (
                <Link
                  to="/auth"
                  className="px-4 py-2.5 rounded-lg text-sm font-medium transition-colors bg-white/10 text-white hover:bg-white/20"
                >
                  <span className="flex items-center gap-1.5">
                    <LogIn className="h-4 w-4" />
                    Entrar
                  </span>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              {user && (
                <button onClick={() => setDrawerOpen(true)} className="focus:outline-none rounded-full">
                  <Avatar className="h-8 w-8 border-2 border-white/20">
                    <AvatarImage src={avatarUrl || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">{getUserInitials()}</AvatarFallback>
                  </Avatar>
                </button>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                className="p-2 rounded-lg transition-all text-white/70 hover:text-white hover:bg-white/10"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4 border-t border-white/10 animate-fade-in">
              <div className="flex flex-col gap-1">
                <Link to="/" onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive("/") ? "bg-primary/15 text-primary font-medium" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                  <Home className="h-5 w-5" /><span>Início</span>
                </Link>
                {mainNavLinks.map((link) => (
                  <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive(link.path) ? "bg-primary/15 text-primary font-medium" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                    {link.icon}<span>{link.label}</span>
                  </Link>
                ))}

                {/* Mobile Ferramentas accordion */}
                <button
                  onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-xl transition-all ${
                    isToolActive ? "bg-primary/15 text-primary font-medium" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Wrench className="h-5 w-5" />
                    <span>Ferramentas do Músico</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileToolsOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileToolsOpen && (
                  <div className="ml-6 flex flex-col gap-0.5 animate-fade-in">
                    {toolsLinks.map((link) => (
                      <Link key={link.path} to={link.path} onClick={() => { setMobileMenuOpen(false); setMobileToolsOpen(false); }}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive(link.path) ? "bg-primary/15 text-primary font-medium" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
                        {link.icon}<span className="text-sm">{link.label}</span>
                      </Link>
                    ))}
                  </div>
                )}

                <Link to="/ouvir-hinos" onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 mx-2 mt-2 rounded-xl font-semibold text-primary-foreground justify-center bg-gradient-to-r from-primary to-[hsl(var(--gold-light))] shadow-[0_4px_15px_hsl(var(--gold)/0.3)]">
                  <Headphones className="h-5 w-5" />
                  Ouvir Hinos
                </Link>

                <button
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5 w-full text-left mt-2"
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  <span>{isDark ? "Modo Claro" : "Modo Escuro"}</span>
                </button>

                <div className="border-t border-white/10 mt-2 pt-2">
                  {user ? (
                    <>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5">
                          <User className="h-5 w-5" /><span>Admin</span>
                        </Link>
                      )}
                      <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-destructive hover:bg-white/5 w-full text-left">
                        <LogIn className="h-5 w-5" /><span>Sair</span>
                      </button>
                    </>
                  ) : (
                    <Link to="/auth" onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5">
                      <LogIn className="h-5 w-5" /><span>Entrar</span>
                    </Link>
                  )}
                </div>
              </div>
            </nav>
          )}
        </div>

        {/* Category Sub-Navigation */}
        <div className="border-t border-white/5 bg-[hsl(var(--navy-dark))]">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              <Link to="/hinos-avulsos"
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                  location.pathname === "/hinos-avulsos" && !activeCategoria
                    ? "bg-primary/20 text-primary border-primary/30 shadow-sm"
                    : "text-white/40 border-transparent hover:text-primary hover:bg-white/5"
                }`}>
                Todos os Hinos
                {categoryCounts.all != null && <span className="ml-1 text-[10px] opacity-60">({categoryCounts.all})</span>}
              </Link>
              {CATEGORIAS_HINOS.filter((cat) => (categoryCounts[cat.value] ?? 0) > 0).map((cat) => (
                <Link key={cat.value} to={`/hinos-avulsos?categoria=${cat.value}`}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                    activeCategoria === cat.value
                      ? "bg-primary/20 text-primary border-primary/30 shadow-sm"
                      : "text-white/40 border-transparent hover:text-primary hover:bg-white/5"
                  }`}>
                  {cat.label}
                  <span className="ml-1 text-[10px] opacity-60">({categoryCounts[cat.value]})</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </header>

      <ProfileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onAvatarChange={(url) => setAvatarUrl(url)} />
    </>
  );
};

export default Header;
